import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { AlertCircle, Loader2, Settings, Target } from 'lucide-react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { AddManualJobNumberDialog } from './AddManualJobNumberDialog';
import type { AddManualJobNumberPayload, ManualJobPassDialogMode } from './AddManualJobNumberDialog';
import { Notification } from '../../../../../components/ui/custom-notification';
import type { DashboardProject } from '../../../types/project';
import { useLimitsData, useManualStatusConfiguration } from '../../../../../redux/WeldData/hooks/useLimitsData';
import {
  findApiConfigurationForJob,
  mapApiConfigurationToUi,
  mapApiResponseToJobNumbers,
  mapLimitsDataToManualStatusConfiguration,
  buildManualStatusSavePayload,
  type LimitsDataForManualStatus,
} from './manualStatusConfigurationMappers';
import {
  buildManualPasses,
  createEmptyPassCounts,
  findDuplicatePassNames,
  generateManualPassNames,
  getSelectedPassBaseNames,
  MANUAL_STATUS_PARAMETERS,
  manualParameterLabel,
  sortManualPasses,
  type ManualPass,
} from './manualStatusConfigurationPasses';
import {
  formatManualJobSelectValue,
  getManualStatusWeldingMachineOptions,
  MANUAL_WELDING_MACHINE_ID,
  manualJobNumbersMatch,
  parseManualJobSelectValue,
} from './manualJobNumberFormat';
import {
  ManualWeldingSettingsFields,
  type ManualWeldingSettingsValues,
} from './ManualWeldingSettingsFields';
import {
  mergeJobConfigurationCache,
  selectManualConfigurationsToSave,
} from './statusConfigurationMultiJobSave';

export type ManualStatusConfigurationHandle = {
  markDirty: () => void;
};

export interface ManualStatusConfigurationValue {
  jobNumber: string;
  weldingSystem: string;
  slopeTimeIn: string;
  slopeTimeOut: string;
  minimumWeldTime: string;
  enabledParameters: Record<string, boolean>;
  passes: ManualPass[];
}

interface ManualStatusConfigurationProps {
  project: DashboardProject;
  canEdit?: boolean;
  isModalOpen?: boolean;
  triggerSave?: boolean;
  onSaveComplete?: () => void;
  onValidationFailure?: () => void;
  onValidationErrorsChange?: (hasErrors: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onEnabledParameterKeysChange?: (enabled: Record<string, boolean>) => void;
  /** When hosted inside Status Configuration — job is chosen in the parent header. */
  hideJobSelector?: boolean;
  /** Hide slope/min weld fields card — parent renders them in Configuration header. */
  hideSettingsCard?: boolean;
  controlledSettings?: ManualWeldingSettingsValues & {
    onSlopeTimeInChange: (value: string) => void;
    onSlopeTimeOutChange: (value: string) => void;
    onMinimumWeldTimeChange: (value: string) => void;
  };
  onSettingsSync?: (settings: ManualWeldingSettingsValues) => void;
  controlledJobNumber?: string;
  openAddJobRequest?: boolean;
  onAddJobRequestHandled?: () => void;
  onManualJobAdded?: (displayJobNumber: string) => void;
  /** Project limits payload from get-limits; when omitted the component loads it. */
  limitsData?: LimitsDataForManualStatus | null;
  limitsLoading?: boolean;
  onRequestLimitsReload?: () => void;
}

/** Empty configuration template for a new job number. */
const createEmptyConfiguration = (
  jobNumber = '',
  passes: ManualPass[] = []
): ManualStatusConfigurationValue => ({
  jobNumber,
  weldingSystem: MANUAL_WELDING_MACHINE_ID,
  slopeTimeIn: '0',
  slopeTimeOut: '0',
  minimumWeldTime: '',
  enabledParameters: defaultEnabledParameters(),
  passes,
});

/** Manual welding is always averaged per pass, so the method/level are fixed. */
const STATUS_CALCULATION_METHOD_LABEL = 'Average Pass';
const CALCULATION_LEVEL = 'Pass Level';
const SLOPE_TIME_LIMIT = 10;

const defaultEnabledParameters = (): Record<string, boolean> =>
  MANUAL_STATUS_PARAMETERS.reduce((enabled, parameter) => {
    enabled[parameter.key] = true;
    return enabled;
  }, {} as Record<string, boolean>);

const limitCellKey = (passId: string, parameterKey: string, bound: 'min' | 'max') =>
  `${passId}:${parameterKey}:${bound}`;

const hasLimitRangeMismatch = (min: string, max: string): boolean => {
  if (min === '' || max === '') return false;
  const minNum = Number(min);
  const maxNum = Number(max);
  if (!Number.isFinite(minNum) || !Number.isFinite(maxNum)) return false;
  if (minNum === 0 && maxNum === 0) return false;
  return minNum >= maxNum;
};

export const ManualStatusConfiguration = forwardRef<
  ManualStatusConfigurationHandle,
  ManualStatusConfigurationProps
>(({
  project,
  canEdit = true,
  isModalOpen = false,
  triggerSave = false,
  onSaveComplete,
  onValidationFailure,
  onValidationErrorsChange,
  onDirtyChange,
  onEnabledParameterKeysChange,
  hideJobSelector = false,
  hideSettingsCard = false,
  controlledSettings,
  onSettingsSync,
  controlledJobNumber,
  openAddJobRequest = false,
  onAddJobRequestHandled,
  onManualJobAdded,
  limitsData: limitsDataProp,
  limitsLoading: limitsLoadingProp,
  onRequestLimitsReload,
}, ref) => {
  const projectId = project?.project?.projectId ? Number(project.project.projectId) : undefined;

  const { limitsData: internalLimitsData, limitsLoading: internalLimitsLoading, loadLimitsData } =
    useLimitsData();
  const {
    saving,
    success,
    error: saveError,
    saveManualStatusConfiguration,
    resetManualStatusConfiguration,
  } = useManualStatusConfiguration();

  const limitsData = limitsDataProp ?? internalLimitsData;
  const limitsLoading = limitsLoadingProp ?? internalLimitsLoading;
  const manualStatusConfiguration = useMemo(
    () => mapLimitsDataToManualStatusConfiguration(limitsData),
    [limitsData]
  );
  const limitsReloadRequestedRef = useRef(false);
  const saveCompleteNotifiedRef = useRef(false);

  const manualWeldingMachines = useMemo(
    () => getManualStatusWeldingMachineOptions(project?.project?.projectTypes),
    [project?.project?.projectTypes]
  );

  const toDisplayJobNumber = useCallback(
    (rawJobNumber: string) =>
      formatManualJobSelectValue(MANUAL_WELDING_MACHINE_ID, rawJobNumber),
    []
  );

  const [jobConfigurations, setJobConfigurations] = useState<
    Record<string, ManualStatusConfigurationValue>
  >({});
  const [jobNumber, setJobNumber] = useState('');
  const [jobNumberOptions, setJobNumberOptions] = useState<
    { value: string; label: string; weldingSystem: string }[]
  >([]);
  const [passDialog, setPassDialog] = useState<{ open: boolean; mode: ManualJobPassDialogMode }>({
    open: false,
    mode: 'add',
  });
  const [waitingForReload, setWaitingForReload] = useState(false);
  const [slopeTimeIn, setSlopeTimeIn] = useState('0');
  const [slopeTimeOut, setSlopeTimeOut] = useState('0');
  const [minimumWeldTime, setMinimumWeldTime] = useState('');
  const [passes, setPasses] = useState<ManualPass[]>([]);
  const [enabledParameters, setEnabledParameters] = useState<Record<string, boolean>>(
    defaultEnabledParameters
  );
  const [invalidCells, setInvalidCells] = useState<Record<string, boolean>>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [dirtyJobKeys, setDirtyJobKeys] = useState<string[]>([]);

  const isJobNumberReadOnly = !canEdit;
  const isFormReadOnly = !canEdit;
  const hasPasses = passes.length > 0;
  const usesControlledSettings = Boolean(controlledSettings);
  const hasUnsavedJobEdits = isDirty || dirtyJobKeys.length > 0;

  const slopeTimeInValue = controlledSettings?.slopeTimeIn ?? slopeTimeIn;
  const slopeTimeOutValue = controlledSettings?.slopeTimeOut ?? slopeTimeOut;
  const minimumWeldTimeValue = controlledSettings?.minimumWeldTime ?? minimumWeldTime;

  const markDirty = useCallback(() => {
    setIsDirty(true);
    setDirtyJobKeys((current) => {
      if (!jobNumber || current.includes(jobNumber)) return current;
      return [...current, jobNumber];
    });
  }, [jobNumber]);

  useImperativeHandle(ref, () => ({ markDirty }), [markDirty]);

  const updateSlopeTimeIn = useCallback(
    (value: string) => {
      if (controlledSettings) controlledSettings.onSlopeTimeInChange(value);
      else setSlopeTimeIn(value);
      markDirty();
    },
    [controlledSettings, markDirty]
  );

  const updateSlopeTimeOut = useCallback(
    (value: string) => {
      if (controlledSettings) controlledSettings.onSlopeTimeOutChange(value);
      else setSlopeTimeOut(value);
      markDirty();
    },
    [controlledSettings, markDirty]
  );

  const updateMinimumWeldTime = useCallback(
    (value: string) => {
      if (controlledSettings) controlledSettings.onMinimumWeldTimeChange(value);
      else setMinimumWeldTime(value);
      markDirty();
    },
    [controlledSettings, markDirty]
  );

  const getCurrentConfiguration = useCallback((): ManualStatusConfigurationValue => {
    const parsed = parseManualJobSelectValue(jobNumber);
    return {
      jobNumber: parsed.jobNumber,
      weldingSystem: MANUAL_WELDING_MACHINE_ID,
      slopeTimeIn: slopeTimeInValue,
      slopeTimeOut: slopeTimeOutValue,
      minimumWeldTime: minimumWeldTimeValue,
      enabledParameters,
      passes,
    };
  }, [
    enabledParameters,
    jobNumber,
    minimumWeldTimeValue,
    passes,
    slopeTimeInValue,
    slopeTimeOutValue,
  ]);

  const saveCurrentJobToCache = useCallback(() => {
    if (!jobNumber) return;
    const current = getCurrentConfiguration();
    setJobConfigurations((currentConfigs) => ({
      ...currentConfigs,
      [jobNumber]: current,
    }));
  }, [getCurrentConfiguration, jobNumber]);

  const applyConfiguration = useCallback((config: ManualStatusConfigurationValue | null | undefined) => {
    const plainJob = config?.jobNumber
      ? config.jobNumber.includes(':')
        ? parseManualJobSelectValue(config.jobNumber).jobNumber
        : config.jobNumber
      : '';
    const displayJobNumber = plainJob ? toDisplayJobNumber(plainJob) : '';
    setJobNumber(displayJobNumber);

    const syncedSettings: ManualWeldingSettingsValues = {
      slopeTimeIn: config?.slopeTimeIn ?? '0',
      slopeTimeOut: config?.slopeTimeOut ?? '0',
      minimumWeldTime: config?.minimumWeldTime ?? '',
    };

    if (usesControlledSettings) {
      onSettingsSync?.(syncedSettings);
    } else {
      setSlopeTimeIn(syncedSettings.slopeTimeIn);
      setSlopeTimeOut(syncedSettings.slopeTimeOut);
      setMinimumWeldTime(syncedSettings.minimumWeldTime);
    }

    setPasses(sortManualPasses(config?.passes ?? []));
    setEnabledParameters(config?.enabledParameters ?? defaultEnabledParameters());
    setInvalidCells({});
    setFormErrors([]);
  }, [onSettingsSync, toDisplayJobNumber, usesControlledSettings]);

  useEffect(() => {
    if (isModalOpen && projectId && limitsDataProp == null) {
      loadLimitsData();
    }
    if (!isModalOpen) {
      resetManualStatusConfiguration();
      limitsReloadRequestedRef.current = false;
      saveCompleteNotifiedRef.current = false;
      setJobConfigurations({});
      setJobNumberOptions([]);
      setJobNumber('');
      setPassDialog({ open: false, mode: 'add' });
      setWaitingForReload(false);
      applyConfiguration(createEmptyConfiguration());
      setIsDirty(false);
      setDirtyJobKeys([]);
    }
  }, [
    applyConfiguration,
    isModalOpen,
    limitsDataProp,
    loadLimitsData,
    projectId,
    resetManualStatusConfiguration,
  ]);

  useEffect(() => {
    if (!manualStatusConfiguration.configurations.length) {
      if (!jobNumber) {
        setJobNumberOptions([]);
      }
      return;
    }

    const apiJobs = mapApiResponseToJobNumbers(manualStatusConfiguration);
    setJobNumberOptions(apiJobs);

    const savedJobKeys = new Set(
      apiJobs.map((job) => job.value.trim().toLowerCase())
    );
    setJobConfigurations((prev) => {
      const fromApi: Record<string, ManualStatusConfigurationValue> = {};
      manualStatusConfiguration.configurations?.forEach((config) => {
        const uiConfig = mapApiConfigurationToUi(config);
        if (uiConfig?.jobNumber) {
          const displayJobNumber = toDisplayJobNumber(uiConfig.jobNumber);
          fromApi[displayJobNumber] = uiConfig;
        }
      });

      const next = { ...fromApi };
      Object.entries(prev).forEach(([key, config]) => {
        const parsed = parseManualJobSelectValue(key);
        const plainKey = parsed.jobNumber.trim().toLowerCase();
        if (!savedJobKeys.has(plainKey)) {
          next[key] = config;
        }
      });
      return next;
    });
  }, [manualStatusConfiguration, toDisplayJobNumber]);

  useEffect(() => {
    if (!waitingForReload || saving) return;

    if (saveError) {
      setWaitingForReload(false);
      limitsReloadRequestedRef.current = false;
      saveCompleteNotifiedRef.current = false;
      onValidationFailure?.();
      return;
    }

    if (!success) return;

    if (!saveCompleteNotifiedRef.current) {
      saveCompleteNotifiedRef.current = true;
      onSaveComplete?.();
    }

    if (!limitsReloadRequestedRef.current) {
      limitsReloadRequestedRef.current = true;
      if (onRequestLimitsReload) {
        onRequestLimitsReload();
      } else {
        loadLimitsData();
      }
    }

    if (limitsLoading) return;

    if (jobNumber) {
      const parsed = parseManualJobSelectValue(jobNumber);
      const apiConfig = findApiConfigurationForJob(manualStatusConfiguration, parsed.jobNumber);
      const uiConfig = mapApiConfigurationToUi(apiConfig);
      if (uiConfig) {
        applyConfiguration(uiConfig);
      }
    }

    setIsDirty(false);
    setWaitingForReload(false);
    limitsReloadRequestedRef.current = false;
    saveCompleteNotifiedRef.current = false;
  }, [
    applyConfiguration,
    jobNumber,
    limitsLoading,
    loadLimitsData,
    manualStatusConfiguration,
    onRequestLimitsReload,
    onSaveComplete,
    onValidationFailure,
    saveError,
    saving,
    success,
    waitingForReload,
  ]);

  useEffect(() => {
    if (!manualStatusConfiguration.configurations.length || !jobNumber || isDirty || waitingForReload) {
      return;
    }
    if (dirtyJobKeys.some((key) => manualJobNumbersMatch(key, jobNumber))) {
      return;
    }
    const parsed = parseManualJobSelectValue(jobNumber);
    const apiConfig = findApiConfigurationForJob(manualStatusConfiguration, parsed.jobNumber);
    const uiConfig = mapApiConfigurationToUi(apiConfig);
    if (uiConfig) {
      applyConfiguration(uiConfig);
    }
  }, [
    applyConfiguration,
    dirtyJobKeys,
    isDirty,
    jobNumber,
    manualStatusConfiguration,
    waitingForReload,
  ]);

  useEffect(() => {
    if (!isModalOpen || limitsLoading || jobNumber || hideJobSelector) return;
    const firstJob = jobNumberOptions[0];
    if (!firstJob) return;
    handleJobNumberChange(formatManualJobSelectValue(MANUAL_WELDING_MACHINE_ID, firstJob.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideJobSelector, isModalOpen, limitsLoading, jobNumber, jobNumberOptions, jobConfigurations]);

  useEffect(() => {
    if (!openAddJobRequest) return;
    if (manualWeldingMachines.length > 0) {
      setPassDialog({ open: true, mode: 'add' });
    }
    onAddJobRequestHandled?.();
  }, [manualWeldingMachines.length, onAddJobRequestHandled, openAddJobRequest]);

  useEffect(() => {
    if (!hideJobSelector || !controlledJobNumber || controlledJobNumber === jobNumber) return;
    handleJobNumberChange(controlledJobNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledJobNumber, hideJobSelector, jobNumber]);

  const handleJobNumberChange = (nextJobNumber: string) => {
    let nextConfigs = jobConfigurations;
    if (jobNumber) {
      const current = getCurrentConfiguration();
      nextConfigs = mergeJobConfigurationCache(jobConfigurations, jobNumber, current);
      setJobConfigurations(nextConfigs);
      if (isDirty) {
        setDirtyJobKeys((currentKeys) =>
          currentKeys.includes(jobNumber) ? currentKeys : [...currentKeys, jobNumber]
        );
      }
    }

    setJobNumber(nextJobNumber);
    const savedConfig = nextConfigs[nextJobNumber];
    if (savedConfig) {
      applyConfiguration(savedConfig);
    } else {
      const parsed = parseManualJobSelectValue(nextJobNumber);
      applyConfiguration(createEmptyConfiguration(parsed.jobNumber));
    }
    setIsDirty(false);
  };

  useEffect(() => {
    onDirtyChange?.(hasUnsavedJobEdits);
  }, [hasUnsavedJobEdits, onDirtyChange]);

  useEffect(() => {
    onEnabledParameterKeysChange?.(enabledParameters);
  }, [enabledParameters, onEnabledParameterKeysChange]);

  const isSavedJobNumber = useCallback(
    (value: string) =>
      jobNumberOptions.some((option) => manualJobNumbersMatch(option.label, value)),
    [jobNumberOptions]
  );

  const searchableJobOptions = useMemo(() => {
    const apiOptions = jobNumberOptions.map((job) => ({
      id: job.label,
      name: job.label,
    }));

    if (jobNumber && !isSavedJobNumber(jobNumber)) {
      return [{ id: `new-${jobNumber}`, name: jobNumber }, ...apiOptions];
    }

    return apiOptions;
  }, [isSavedJobNumber, jobNumber, jobNumberOptions]);

  const handleAddNewJobNumber = ({
    jobNumber: rawJobNumber,
    selectedPasses,
  }: AddManualJobNumberPayload) => {
    const displayValue = formatManualJobSelectValue(MANUAL_WELDING_MACHINE_ID, rawJobNumber);
    if (!displayValue || selectedPasses.length === 0) return;

    if (
      isSavedJobNumber(displayValue) ||
      Object.keys(jobConfigurations).some((key) => manualJobNumbersMatch(key, displayValue))
    ) {
      Notification.warning('That job number already exists.');
      return;
    }

    if (jobNumber) {
      saveCurrentJobToCache();
    }

    const passCounts = createEmptyPassCounts();
    selectedPasses.forEach((baseName) => {
      passCounts[baseName] = 1;
    });
    const initialPasses = buildManualPasses(generateManualPassNames(passCounts), []);
    const newConfiguration = createEmptyConfiguration(rawJobNumber, initialPasses);

    setJobNumber(displayValue);
    applyConfiguration(newConfiguration);
    setJobConfigurations((current) => ({
      ...current,
      [displayValue]: newConfiguration,
    }));
    setIsDirty(true);
    setDirtyJobKeys((current) =>
      current.includes(displayValue) ? current : [...current, displayValue]
    );
    onManualJobAdded?.(displayValue);
  };

  const applySelectedPasses = useCallback(
    (selectedPasses: AddManualJobNumberPayload['selectedPasses']) => {
      const passCounts = createEmptyPassCounts();
      selectedPasses.forEach((baseName) => {
        passCounts[baseName] = 1;
      });
      // Always one column per base pass group — never regenerate numbered duplicates on re-check.
      return buildManualPasses(generateManualPassNames(passCounts), passes);
    },
    [passes]
  );

  const handleEditPasses = ({ selectedPasses }: AddManualJobNumberPayload) => {
    if (!jobNumber || selectedPasses.length === 0) return;

    const updatedPasses = applySelectedPasses(selectedPasses);
    setPasses(updatedPasses);
    markDirty();

    const current = getCurrentConfiguration();
    setJobConfigurations((currentConfigs) => ({
      ...currentConfigs,
      [jobNumber]: {
        ...current,
        passes: updatedPasses,
      },
    }));
  };

  const handlePassDialogSave = (payload: AddManualJobNumberPayload) => {
    if (passDialog.mode === 'edit') {
      handleEditPasses(payload);
      return;
    }
    handleAddNewJobNumber(payload);
  };

  const editPassDialogJobNumber = useMemo(
    () => parseManualJobSelectValue(jobNumber).jobNumber,
    [jobNumber]
  );

  const editPassDialogSelection = useMemo(
    () => getSelectedPassBaseNames(passes),
    [passes]
  );

  const handleParameterEnabledChange = (parameterKey: string, enabled: boolean) => {
    setEnabledParameters((current) => ({ ...current, [parameterKey]: enabled }));
    markDirty();
  };

  const handleLimitChange = (
    passId: string,
    parameterKey: string,
    bound: 'min' | 'max',
    value: string
  ) => {
    setPasses((current) =>
      current.map((pass) =>
        pass.id === passId
          ? {
              ...pass,
              limits: {
                ...pass.limits,
                [parameterKey]: { ...pass.limits[parameterKey], [bound]: value },
              },
            }
          : pass
      )
    );
    setInvalidCells((cells) => {
      const next = { ...cells };
      delete next[limitCellKey(passId, parameterKey, bound)];
      return next;
    });
    markDirty();
  };

  const validateConfiguration = useCallback(
    (
      config: ManualStatusConfigurationValue,
      options?: { markCells?: boolean; jobLabel?: string }
    ): { errors: string[]; cells: Record<string, boolean> } => {
      const errors: string[] = [];
      const cells: Record<string, boolean> = {};
      const prefix = options?.jobLabel ? `${options.jobLabel}: ` : '';

      if (!config.jobNumber?.trim()) errors.push(`${prefix}Select a job number.`);
      if (!config.passes.length) errors.push(`${prefix}Add at least one pass before saving.`);

      const outOfRange = (value: string) =>
        value !== '' && Math.abs(Number(value)) > SLOPE_TIME_LIMIT;
      if (outOfRange(config.slopeTimeIn) || outOfRange(config.slopeTimeOut)) {
        errors.push(
          `${prefix}Slope time must be between -${SLOPE_TIME_LIMIT} and ${SLOPE_TIME_LIMIT} seconds.`
        );
      }
      if (config.minimumWeldTime !== '' && Number(config.minimumWeldTime) < 0) {
        errors.push(`${prefix}Minimum weld time cannot be negative.`);
      }

      if (config.passes.some((pass) => !pass.name.trim())) {
        errors.push(`${prefix}Every pass needs a name.`);
      }
      const duplicates = findDuplicatePassNames(config.passes);
      if (duplicates.length > 0) {
        errors.push(`${prefix}Duplicate pass names: ${duplicates.join(', ')}.`);
      }

      config.passes.forEach((pass) => {
        MANUAL_STATUS_PARAMETERS.forEach((parameter) => {
          if (!config.enabledParameters[parameter.key]) return;
          const { min, max } = pass.limits[parameter.key] ?? { min: '', max: '' };
          const label = manualParameterLabel(parameter);

          if (hasLimitRangeMismatch(min, max)) {
            if (options?.markCells) {
              cells[limitCellKey(pass.id, parameter.key, 'min')] = true;
              cells[limitCellKey(pass.id, parameter.key, 'max')] = true;
            }
            errors.push(`${prefix}${pass.name} · ${label}: min must be less than max.`);
          }
        });
      });

      return { errors, cells };
    },
    []
  );

  const previousTriggerSave = useRef(false);

  const handleSave = useCallback(() => {
    if (!projectId) {
      onValidationFailure?.();
      return;
    }

    const current = getCurrentConfiguration();
    const allConfigurations = mergeJobConfigurationCache(
      jobConfigurations,
      jobNumber,
      current
    );
    setJobConfigurations(allConfigurations);

    const configurationsToSave = selectManualConfigurationsToSave(
      allConfigurations,
      dirtyJobKeys,
      jobNumber,
      isDirty
    );

    if (configurationsToSave.length === 0) {
      onValidationFailure?.();
      return;
    }

    const aggregatedErrors: string[] = [];
    let activeCells: Record<string, boolean> = {};

    configurationsToSave.forEach((config) => {
      const isActiveJob =
        Boolean(jobNumber) &&
        (manualJobNumbersMatch(jobNumber, toDisplayJobNumber(config.jobNumber)) ||
          parseManualJobSelectValue(jobNumber).jobNumber.toLowerCase() ===
            config.jobNumber.trim().toLowerCase());
      const jobLabel =
        configurationsToSave.length > 1
          ? toDisplayJobNumber(config.jobNumber)
          : undefined;
      const { errors, cells } = validateConfiguration(config, {
        markCells: isActiveJob,
        jobLabel,
      });
      aggregatedErrors.push(...errors);
      if (isActiveJob) {
        activeCells = cells;
      }
    });

    setFormErrors(aggregatedErrors);
    setInvalidCells(activeCells);
    onValidationErrorsChange?.(aggregatedErrors.length > 0);

    if (aggregatedErrors.length > 0) {
      onValidationFailure?.();
      return;
    }

    saveManualStatusConfiguration(
      projectId,
      configurationsToSave.map((config) =>
        buildManualStatusSavePayload(config, limitsData)
      )
    );
    setDirtyJobKeys([]);
    setIsDirty(false);
    saveCompleteNotifiedRef.current = false;
    limitsReloadRequestedRef.current = false;
    setWaitingForReload(true);
  }, [
    dirtyJobKeys,
    getCurrentConfiguration,
    isDirty,
    jobConfigurations,
    jobNumber,
    limitsData,
    onValidationErrorsChange,
    onValidationFailure,
    projectId,
    saveManualStatusConfiguration,
    toDisplayJobNumber,
    validateConfiguration,
  ]);

  useEffect(() => {
    if (!triggerSave || previousTriggerSave.current) {
      previousTriggerSave.current = triggerSave;
      return;
    }
    previousTriggerSave.current = triggerSave;
    handleSave();
  }, [handleSave, triggerSave]);

  if (limitsLoading && isModalOpen && !jobNumber && !hideJobSelector) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#EB1C37]" />
        <span className="ml-2 text-gray-600">Loading manual status configuration data...</span>
      </div>
    );
  }

  const isSavingOrReloading = !hideSettingsCard && (saving || waitingForReload);

  return (
    <>
    <div className="relative space-y-4 w-full max-w-full min-w-0">
      {isSavingOrReloading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-md bg-white/70">
          <div className="flex items-center gap-2 text-gray-600">
            <Loader2 className="h-5 w-5 animate-spin text-[#EB1C37]" />
            <span className="text-sm">Saving and reloading configuration...</span>
          </div>
        </div>
      )}
      {formErrors.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-readable-red/30 bg-red-50 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-readable-red mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-[12px] font-semibold text-readable-red">
              Resolve {formErrors.length} issue{formErrors.length === 1 ? '' : 's'} before saving
            </p>
            <ul className="space-y-0.5">
              {formErrors.slice(0, 6).map((error) => (
                <li key={error} className="text-[12px] text-text-grey">
                  {error}
                </li>
              ))}
              {formErrors.length > 6 && (
                <li className="text-[12px] text-heritage-grey">
                  and {formErrors.length - 6} more…
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {!hideSettingsCard && (
      <Card className="border border-[#E2E5EA] shadow-sm max-w-full overflow-visible">
        <CardContent className="p-4 overflow-visible">
          <h3 className="text-[14px] font-semibold text-blueprint-blue mb-4">Configuration</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {!hideJobSelector && (
            <div className="space-y-1.5 min-w-0">
              <Label className="text-[12px] font-medium text-text-grey">Job Number</Label>
              <SearchableSelect
                value={jobNumber || undefined}
                onValueChange={handleJobNumberChange}
                placeholder="Select job number"
                searchPlaceholder="Search job numbers..."
                options={searchableJobOptions}
                onAddNew={() => {
                  if (manualWeldingMachines.length > 0) {
                    setPassDialog({ open: true, mode: 'add' });
                  }
                }}
                addNewText="Add New Job Number"
                showAddButton={canEdit}
                disabled={isJobNumberReadOnly || isSavingOrReloading}
                loading={limitsLoading || isSavingOrReloading}
                loadingText="Loading job numbers..."
              />
              {jobNumber && !isSavedJobNumber(jobNumber) && (
                <p className="text-[11px] text-heritage-grey">
                  New job number — configure passes and limits, then save to add it to the list.
                </p>
              )}
            </div>
            )}

            {!hideJobSelector && (
            <>
            <div className="space-y-1.5 min-w-0">
              <Label className="text-[12px] font-medium text-text-grey">
                Status Calculation Method
              </Label>
              <div className="flex h-9 items-center rounded-md border border-[#E2E5EA] bg-page-bg px-3 text-[12px] font-medium text-blueprint-blue">
                {STATUS_CALCULATION_METHOD_LABEL}
              </div>
            </div>

            <div className="space-y-1.5 min-w-0">
              <Label className="text-[12px] font-medium text-text-grey">Calculation Level</Label>
              <div className="flex h-9 items-center rounded-md border border-[#E2E5EA] bg-page-bg px-3 text-[12px] font-medium text-blueprint-blue">
                {CALCULATION_LEVEL}
              </div>
            </div>
            </>
            )}

            <ManualWeldingSettingsFields
              slopeTimeIn={slopeTimeInValue}
              slopeTimeOut={slopeTimeOutValue}
              minimumWeldTime={minimumWeldTimeValue}
              disabled={isFormReadOnly}
              onSlopeTimeInChange={updateSlopeTimeIn}
              onSlopeTimeOutChange={updateSlopeTimeOut}
              onMinimumWeldTimeChange={updateMinimumWeldTime}
            />
          </div>
        </CardContent>
      </Card>
      )}

      <Card
        className="border border-[#E2E5EA] shadow-sm max-w-full overflow-hidden"
        style={{ maxWidth: '100%' }}
      >
        <CardContent className="p-0 max-w-full overflow-hidden" style={{ maxWidth: '100%' }}>
          <div className="flex items-start justify-between gap-3 border-b border-[#E2E5EA] bg-page-bg py-3 pl-4 pr-8">
            <div className="min-w-0 flex-1">
              <h3 className="text-[14px] font-semibold text-blueprint-blue">
                Status Configuration Parameters
              </h3>
              {hasPasses && (
                <p className="text-[11px] text-heritage-grey mt-0.5">
                  {passes.length} pass{passes.length === 1 ? '' : 'es'}:{' '}
                  {passes.map((pass) => pass.name).join(', ')}
                </p>
              )}
            </div>
            {canEdit && jobNumber && (
              <Button
                type="button"
                //variant="outline"
                size="sm"
                className="mb-0.5 h-8 shrink-0 px-3 text-xs mr-2"
                onClick={() => setPassDialog({ open: true, mode: 'edit' })}
              >
                Edit Passes
              </Button>
            )}
          </div>

          {hasPasses ? (
            <div className="flex flex-col w-full max-w-full overflow-hidden">
              <div
                className="w-full overflow-x-auto overflow-y-visible table-scroll"
                style={{ maxWidth: '100%' }}
              >
                <div className="inline-flex min-w-max">
                  <div className="sticky left-0 z-20 bg-white shadow-lg">
                    <table className="border-collapse h-full">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-[#E2E5EA] h-[50px] sm:h-[60px] bg-page-bg">
                          <th className="w-[220px] sm:w-[260px] px-2 sm:px-4 text-left text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span>Parameter</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {MANUAL_STATUS_PARAMETERS.map((parameter, index) => (
                          <tr
                            key={parameter.key}
                            className={`h-[48px] sm:h-[52px] ${
                              index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'
                            } ${
                              index < MANUAL_STATUS_PARAMETERS.length - 1
                                ? 'border-b border-[#EEF0F3]'
                                : ''
                            }`}
                          >
                            <td className="w-[220px] sm:w-[260px] px-2 sm:px-4 text-left border-r border-[#E2E5EA]">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <Checkbox
                                  id={`manual-status-${parameter.key}`}
                                  checked={Boolean(enabledParameters[parameter.key])}
                                  onCheckedChange={(checked) =>
                                    handleParameterEnabledChange(parameter.key, checked === true)
                                  }
                                  disabled={isFormReadOnly}
                                  className="data-[state=checked]:bg-hero-red data-[state=checked]:border-hero-red data-[state=checked]:text-white h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0"
                                />
                                <span className="text-[12px] text-text-grey leading-tight">
                                  {manualParameterLabel(parameter)}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="overflow-x-auto flex-1 min-w-0 w-full"
                    style={{ maxWidth: 'calc(100vw - 260px)' }}
                  >
                    <table className="border-collapse h-full min-w-max">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-[#E2E5EA] h-[50px] sm:h-[60px] bg-page-bg">
                          {passes.map((pass, index) => (
                            <th
                              key={pass.id}
                              className={`px-1 xs:px-2 sm:px-4 min-w-[140px] xs:min-w-[160px] sm:min-w-[180px] md:min-w-[220px] ${
                                index === 0 ? '' : 'border-l border-[#E2E5EA]'
                              }`}
                            >
                              <div className="h-[28px] sm:h-[36px] flex items-center justify-center text-center text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                                <div className="flex items-center gap-1 min-w-0">
                                  <Target className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                                  <span className="truncate">{pass.name || 'Untitled'}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-1 sm:gap-2 text-[10px] font-bold uppercase tracking-wider text-center text-heritage-grey">
                                <div className="py-1">Min</div>
                                <div className="py-1">Max</div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {MANUAL_STATUS_PARAMETERS.map((parameter, rowIndex) => (
                          <tr
                            key={parameter.key}
                            className={`h-[48px] sm:h-[52px] ${
                              rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'
                            } ${
                              rowIndex < MANUAL_STATUS_PARAMETERS.length - 1
                                ? 'border-b border-[#EEF0F3]'
                                : ''
                            }`}
                          >
                            {passes.map((pass, colIndex) => {
                              const limit = pass.limits[parameter.key] ?? { min: '', max: '' };
                              const disabled = isFormReadOnly || !enabledParameters[parameter.key];

                              return (
                                <td
                                  key={pass.id}
                                  className={`px-1 xs:px-2 sm:px-4 py-1 sm:py-2 h-[48px] sm:h-[52px] text-xs ${
                                    colIndex === 0 ? '' : 'border-l border-[#E2E5EA]'
                                  }`}
                                >
                                  <div className="grid grid-cols-2 gap-1 sm:gap-3 h-full">
                                    {(['min', 'max'] as const).map((bound) => (
                                      <div
                                        key={bound}
                                        className="flex items-center justify-center h-full"
                                      >
                                        <Input
                                          type="number"
                                          value={limit[bound]}
                                          onChange={(event) =>
                                            handleLimitChange(
                                              pass.id,
                                              parameter.key,
                                              bound,
                                              event.target.value
                                            )
                                          }
                                          disabled={disabled}
                                          className={`w-full text-center text-xs sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 ${
                                            invalidCells[limitCellKey(pass.id, parameter.key, bound)]
                                              ? 'bg-red-50 focus:ring-red-500'
                                              : 'border-[#E2E5EA] focus:ring-hero-red/30'
                                          }`}
                                          step="0.01"
                                          placeholder={bound === 'min' ? 'Min' : 'Max'}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-14 text-center">
              <p className="text-[13px] text-text-grey">No passes defined yet.</p>
              <p className="text-[12px] text-heritage-grey mt-1">
                Use Add New Job Number to select passes, then enter high/low limits.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

    </div>

    <AddManualJobNumberDialog
      open={passDialog.open}
      onOpenChange={(open) => setPassDialog((current) => ({ ...current, open }))}
      mode={passDialog.mode}
      initialJobNumber={editPassDialogJobNumber}
      initialSelectedPasses={editPassDialogSelection}
      onSave={handlePassDialogSave}
    />
    </>
  );
});

ManualStatusConfiguration.displayName = 'ManualStatusConfiguration';
